/**
 * Open enrollment window helpers, shared by the Accounts header badge and the
 * account's Group Info editor so both read the window the same way.
 *
 * The dates live on `GroupMetadata.openEnrollmentStartDate/EndDate` and are
 * date-only values anchored at UTC noon by `parseCalendarDate`. Comparisons are
 * therefore done on calendar days (UTC getters for the stored dates, local
 * getters for "today") rather than on instants — the window is inclusive of both
 * endpoints in the viewer's own calendar.
 */

import {formatDate} from './utils';

export interface OpenEnrollmentWindow {
  startDate: Date | string | null | undefined;
  endDate: Date | string | null | undefined;
}

/** The `GroupMetadata` columns the window is stored in. */
export interface OpenEnrollmentMetadata {
  openEnrollmentStartDate: Date | string | null;
  openEnrollmentEndDate: Date | string | null;
}

/** Adapt a group's metadata row to the window shape the helpers below take. */
export function toOpenEnrollmentWindow(
  metadata: OpenEnrollmentMetadata | null | undefined,
): OpenEnrollmentWindow | null {
  if (!metadata) return null;
  return {
    startDate: metadata.openEnrollmentStartDate,
    endDate: metadata.openEnrollmentEndDate,
  };
}

export type OpenEnrollmentStatus =
  | 'NONE'
  /** Only one endpoint is set, so there is no window to evaluate. */
  | 'INCOMPLETE'
  | 'UPCOMING'
  | 'ACTIVE'
  | 'ENDED';

/** Days since the epoch for a date-only value, read with UTC getters. */
function storedCalendarDay(value: Date | string | null | undefined): number | null {
  if (!value) return null;
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return null;
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) / 86_400_000;
}

/** Days since the epoch for the viewer's current calendar date. */
function localCalendarDay(now: Date): number {
  return Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()) / 86_400_000;
}

export function resolveOpenEnrollmentStatus(
  window: OpenEnrollmentWindow,
  now: Date = new Date(),
): OpenEnrollmentStatus {
  const start = storedCalendarDay(window.startDate);
  const end = storedCalendarDay(window.endDate);
  if (start === null && end === null) return 'NONE';
  // Groups whose window predates the end-date field have a start only; treat
  // that as unset rather than inventing an open-ended window.
  if (start === null || end === null) return 'INCOMPLETE';
  const today = localCalendarDay(now);
  if (today < start) return 'UPCOMING';
  if (today > end) return 'ENDED';
  return 'ACTIVE';
}

export function isOpenEnrollmentActive(
  window: OpenEnrollmentWindow,
  now: Date = new Date(),
): boolean {
  return resolveOpenEnrollmentStatus(window, now) === 'ACTIVE';
}

/**
 * `Aug 1, 2026 - Aug 31, 2026`, or an empty string when either endpoint is
 * missing. Callers that need to render a partial window format the endpoints
 * themselves.
 */
export function formatOpenEnrollmentRange(window: OpenEnrollmentWindow): string {
  if (!window.startDate || !window.endDate) return '';
  return `${formatDate(window.startDate, 'MMM D, YYYY')} - ${formatDate(
    window.endDate,
    'MMM D, YYYY',
  )}`;
}
